import { useTreasureApi, ApiKeys } from 'hooks/api/use-treasure-api';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from 'react-query';
import { ReactQueryKeys } from 'enums';
import { ApprovalPolicy } from '../../team/hooks/use-approval-policy';

declare const centsBrand: unique symbol;
declare const dollarsBrand: unique symbol;

/** Integer cents. $1 is 100. A plain number is not assignable. */
export type Cents = number & { readonly [centsBrand]: 'cents' };

/** Decimal dollars. $1 is 1. A plain number is not assignable. */
export type Dollars = number & { readonly [dollarsBrand]: 'dollars' };

const asCents = (value: number): Cents => value as Cents;
const asDollars = (value: number): Dollars => value as Dollars;

export type ScheduledWithdrawalPayload = {
  kind: 'schedule_out';
  /** Integer cents, when the gateway stored it. Absent on older approvals. */
  amountCents?: Cents;
  body?: {
    amountDollars?: Dollars;
    bankAccountId?: string;
  };
};

export type CentWithdrawalPayload = {
  kind: 'create_withdrawal' | 'sweep_withdrawal';
  /** Integer cents, when the gateway stored it. Absent on older approvals. */
  amountCents?: Cents;
  body?: {
    amountCents?: Cents;
    bankAccountId?: string;
  };
};

export type OtherApprovalPayload = {
  kind: 'other';
  body?: {
    person?: {
      email?: string;
    };
    bankAccountId?: string;
    cash?: AllocationSlice;
    smart?: AllocationSlice;
    tbills?: AllocationSlice;
    moneyMarket?: AllocationSlice;
  };
};

export type ApprovalRequestPayload =
  | ScheduledWithdrawalPayload
  | CentWithdrawalPayload
  | OtherApprovalPayload;

interface WireApprovalPayload {
  amountCents?: unknown;
  kind?: unknown;
  body?: {
    amount?: unknown;
    person?: {
      email?: unknown;
    };
    bankAccountId?: unknown;
    cash?: AllocationSlice;
    smart?: AllocationSlice;
    tbills?: AllocationSlice;
    moneyMarket?: AllocationSlice;
  };
}

const finiteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const stringValue = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/**
 * The wire field is `body.amount` for every withdrawal. This is the only
 * place that decides its unit: schedule_out is dollars, and the other
 * withdrawal kinds are cents.
 */
export const parseApprovalRequestPayload = (
  raw: unknown,
): ApprovalRequestPayload | null => {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }

  const wire = raw as WireApprovalPayload;
  const storedAmountCents = finiteNumber(wire.amountCents);
  const bodyAmount = finiteNumber(wire.body?.amount);
  const bankAccountId = stringValue(wire.body?.bankAccountId);

  if (wire.kind === 'schedule_out') {
    return {
      kind: 'schedule_out',
      amountCents:
        storedAmountCents === undefined
          ? undefined
          : asCents(storedAmountCents),
      body: {
        amountDollars:
          bodyAmount === undefined ? undefined : asDollars(bodyAmount),
        bankAccountId,
      },
    };
  }

  if (wire.kind === 'create_withdrawal' || wire.kind === 'sweep_withdrawal') {
    return {
      kind: wire.kind,
      amountCents:
        storedAmountCents === undefined
          ? undefined
          : asCents(storedAmountCents),
      body: {
        amountCents: bodyAmount === undefined ? undefined : asCents(bodyAmount),
        bankAccountId,
      },
    };
  }

  return {
    kind: 'other',
    body: {
      person:
        typeof wire.body?.person?.email === 'string'
          ? { email: wire.body.person.email }
          : undefined,
      bankAccountId,
      cash: wire.body?.cash,
      smart: wire.body?.smart,
      tbills: wire.body?.tbills,
      moneyMarket: wire.body?.moneyMarket,
    },
  };
};

interface AllocationSlice {
  amount?: number;
  percentage?: number;
}

export interface ApprovalRequest {
  requestId: string;
  initiatedBy: string;
  requestType: string;
  payload: ApprovalRequestPayload | null;
  status: string;
  expiresAt: string;
  targetId: string | null;
}

export interface ApprovalRequestList {
  items: ApprovalRequest[];
}

export const useApprovalRequests = (): UseQueryResult<ApprovalRequestList> => {
  const { treasureApi } = useTreasureApi(ApiKeys.PortalGateway);

  return useQuery(ReactQueryKeys.ApprovalRequests, async () => {
    const { data } = await treasureApi().get<ApprovalRequestList>(
      '/approval-requests',
    );

    return {
      items: (data.items ?? []).map((item) => ({
        ...item,
        payload: parseApprovalRequestPayload(item.payload),
      })),
    };
  });
};

const ENABLE_MULTIPLE_APPROVAL = 'ENABLE_MULTIPLE_APPROVAL';
const DISABLE_MULTIPLE_APPROVAL = 'DISABLE_MULTIPLE_APPROVAL';

const useApprovalRequestAction = (
  action: 'approve' | 'cancel',
): UseMutationResult<void, unknown, { requestId: string }> => {
  const queryClient = useQueryClient();
  const { treasureApi } = useTreasureApi(ApiKeys.PortalGateway);

  return useMutation(
    async ({ requestId }: { requestId: string }) => {
      await treasureApi().post(`/approval-requests/${requestId}/${action}`);
    },
    {
      onSuccess: (_data, { requestId }) => {
        const list = queryClient.getQueryData<ApprovalRequestList>(
          ReactQueryKeys.ApprovalRequests,
        );
        const requestType = list?.items.find(
          (item) => item.requestId === requestId,
        )?.requestType;
        const changesPolicy =
          requestType === ENABLE_MULTIPLE_APPROVAL ||
          requestType === DISABLE_MULTIPLE_APPROVAL;

        if (changesPolicy) {
          queryClient.setQueryData<ApprovalPolicy | undefined>(
            ReactQueryKeys.ApprovalPolicy,
            (current) => {
              if (!current || current.pendingRequestType !== requestType) {
                return current;
              }

              if (action === 'cancel') {
                return {
                  ...current,
                  pendingRequestType: null,
                };
              }

              return {
                ...current,
                enabled: requestType === ENABLE_MULTIPLE_APPROVAL,
                pendingRequestType: null,
              };
            },
          );
        }

        queryClient.refetchQueries(ReactQueryKeys.ApprovalRequests);
        queryClient.refetchQueries(ReactQueryKeys.ApprovalPolicy);

        if (action === 'approve' && requestType === 'REALLOCATION') {
          queryClient.refetchQueries(ReactQueryKeys.Portfolios);
        }
      },
    },
  );
};

export const useApproveApprovalRequest = (): UseMutationResult<
  void,
  unknown,
  { requestId: string }
> => useApprovalRequestAction('approve');

export const useCancelApprovalRequest = (): UseMutationResult<
  void,
  unknown,
  { requestId: string }
> => useApprovalRequestAction('cancel');
